import { Injectable } from '@angular/core';
import { Subject } from 'rxjs';

import  { ScaleDevice } from '@app/modules/model/scale';

import { AppConstants } from '@app/app.constants';
import { GenericUtils } from '@app/app.commonutils';

import { httpService } from '@app/modules/common/http';
import { syncService } from '@app/modules/sync';

/* allows deactivating verbose logs */
class CustomLogger {
    private _verbose = false;
    addinfo(message){
        if (this._verbose){
            console.info(message);
        }
    }

    addwarn(message){
        if (this._verbose){
            console.warn(message);
        }
    }
}

/*****************************/
/* CONNECTION ABSTRACT CLASS */
/*****************************/

export abstract class WSConnection extends CustomLogger {
    protected _service: _Service = null; 
    get service() : _Service{
        return this._service;
    }

    set service(value: _Service){
        this._service = value;;
    }
    
    protected _version: string = null;       
    get version(){
        return this._version;
    }

    set version(value){
        this._version = value;
    }
    
    protected _svcname: string = null;
    get svcname(){
        return this._svcname;
    }

    set svcname(value){
        this._svcname = value;
    }

    private _contype: 'WSL' | 'WSR' = null;
    get contype(){
        return this._contype;
    }

    protected _onDisconnected = new Subject<any> ();  
    public OnDisconnected = this._onDisconnected.asObservable(); 

    protected _onMessageSent = new Subject<any> ();  
    public OnMessageSent = this._onMessageSent.asObservable();  

    protected _onMessageRecv = new Subject<any> ();    
    public OnMessageRecv = this._onMessageRecv.asObservable();  

    constructor(type, name){
        super();

        this._svcname = name;
        this._contype = type;
    }    

    Disconnect(){
        this._onDisconnected.next();
    }

    // ¿El socket sigue vivo y reutilizable? Por defecto no (solo el socket local
    // WSConnectionL, que envuelve un WebSocket real, lo sobreescribe). Lo usa el
    // pool de deviceService para decidir si reaprovecha una conexion ya abierta.
    IsOpen(): boolean {
        return false;
    }

    // Liberacion GESTIONADA: si el pool marca esta conexion como compartida, fija
    // aqui un callback. Entonces un `Disconnect()` directo de cualquier dueno
    // (WSCacheStorage.OnDestroy, print, etc.) NO cierra el socket a lo bruto sino
    // que baja el refcount del pool -> no tumba el socket que otros aun usan. El
    // cierre real (refcount 0) lo hace el pool tras limpiar este callback.
    protected _managedRelease: (conn: WSConnection) => void = null;
    SetManagedRelease(fn: (conn: WSConnection) => void){
        this._managedRelease = fn;
    }

    // Methods to pause/resume keepalive (override in subclasses that support it)
    PauseKeepAlive(){
        // Override in subclasses that support keepalive
    }
    
    ResumeKeepAlive(){
        // Override in subclasses that support keepalive
    }

    abstract Send(module, message);
}

/*****************************/
/* CACHE SECURE STORAGE      */
/*****************************/

export class WSCacheStorage {
    private _connection = null; 
    private _target = null;

    private _recv_subscription = null;    
    private _conn_subscription = null;

    protected _onDisconnected = new Subject<any> ();  
    public OnDisconnected = this._onDisconnected.asObservable(); 

    constructor(connection: WSConnection){
        if (connection.contype != 'WSL'){
            console.error("[" + connection.contype + "] cannot read from remote websocket connection")
        }
        else {
            this._connection = connection;
            this._conn_subscription = connection.OnDisconnected.subscribe(
            data => {
                this._onDisconnected.next();
            });

            this._target = 'CACHE';
        }
    }

    OnDestroy(){
        if (this._recv_subscription){
            this._recv_subscription.unsubscribe();
            this._recv_subscription = null;
        }

        if (this._conn_subscription){
            this._conn_subscription.unsubscribe();
            this._conn_subscription = null;
        }  
        
        if (this._connection){
            this._connection.Disconnect();
            this._connection = null;
        }
    }

    async Set(key, value){
        if (!this._connection){
            return null;    // not connected
        }

        let _action = {
            command: 'SET',
            key: key,
            value: value
        };

        let _response = await this._connection.Send(this._target, _action);
        if (!_response || _response.exec != 'SUCCESS'){
            // _response puede ser null (WS caido a mitad de operacion):
            // acceder a .data lanzaba TypeError → la promise rechazaba →
            // el Promise.all de _SetStorage descartaba TAMBIEN el write al
            // browser storage. Null-safe obligatorio.
            console.error("[WS STORE]: Set operation failed! ", _response ? _response.data : 'no response')
        }

        return null;
    }

    async Get(key){
        // Tri-estado: "sin datos" (SUCCESS con data vacia) devuelve null;
        // FALLO de lectura (desconectado, timeout, no-SUCCESS) LANZA.
        // Sin esta distincion, un fallo transitorio se confundia con "la
        // copia WS esta vacia" y el siguiente SetChanges la machacaba —
        // perdiendo pendientes que solo vivian en el WS. El unico consumer
        // (_GetStorageDual / SetChanges retry en store.ts) captura la
        // excepcion y la trata como "copia WS ilegible, no sobreescribir".
        if (!this._connection){
            throw new Error('ws-store-not-connected');
        }

        let _action = {
            command: 'GET',
            key: key
        };

        let _response = await this._connection.Send(this._target, _action);
        if (!_response || _response.exec != 'SUCCESS'){
            console.error("[WS STORE]: Get operation failed! ", _response ? _response.data : 'no response')
            throw new Error('ws-store-get-failed');
        }

        return _response.data;
    }

    async Del(key){
        if (!this._connection){
            return null;    // not connected
        }

        let _action = {
            command: 'DEL',
            key: key
        };

        let _response = await this._connection.Send(this._target, _action);
        if (!_response || _response.exec != 'SUCCESS'){
            console.error("[WS STORE]: Del operation failed! ", _response ? _response.data : 'no response')
        }

        return null;
    }    
}

/*****************************/
/* INPUT ABSTRACT CLASS      */
/*****************************/

export abstract class WSInput {
    private _connection = null; 
    private _target = null;
    private _inputid = null;

    private _read_subscription = null;
    private _conn_subscription = null;

    protected _onInputRead = new Subject<string> ();    
    public OnInputRead = this._onInputRead.asObservable();  
    protected _onInputLost = new Subject<string> ();    
    public OnInputLost = this._onInputLost.asObservable();  

    constructor(connection, target){
        if (connection.contype != 'WSL'){
            console.error("[" + connection.contype + "] cannot read from remote websocket connection")
        }
        else {
            this._connection = connection;
            this._conn_subscription = connection.OnDisconnected.subscribe(
            data => {
                this._onInputLost.next();
            });

            this._target = target;    
        }
    }  

    abstract doIniRead();
    abstract doEndRead();

    async OnDestroy(){
        await this.doEndRead();

        if (this._read_subscription){
            this._read_subscription.unsubscribe();
            this._read_subscription = null;
        }

        if (this._conn_subscription){
            this._conn_subscription.unsubscribe();
            this._conn_subscription = null;
        }  
        
        if (this._connection){
            this._connection.Disconnect();
            this._connection = null;
        }
    }

    /***************************/
    /* PROTECTED METHODS       */
    /***************************/

    protected async _doIniRead(_action){
        if (!this._connection){
            return null;    // not connected
        }

        let _response = await this._connection.Send(this._target, _action);
        if (_response && _response.exec == 'SUCCESS'){
            this._inputid = _response.data;

            console.info("[" + this._connection.contype + "] starting read on target: '" + this._target + "' with session (" + this._inputid + ")");
            if (this._read_subscription){
                this._read_subscription.unsubscribe();
                this._read_subscription = null;
            }

            this._read_subscription = this._connection.OnMessageRecv.subscribe(
            data => {
                if (data['uuid'] == this._inputid){
                    this._onInputRead.next(data['data']);    
                }
            });


            return _response;
        }
        else {
            console.error("[" + this._connection.contype + "] read request to target '" + this._target + "' failed! (" + (_response ? _response.data : 'no response') + ")")
            return null;
        }
    }

    protected async _doEndRead(_action){
        if (this._read_subscription){
            this._read_subscription.unsubscribe();
            this._read_subscription = null;
        }

        let _response = await this._connection.Send(this._target, _action);
        if (_response && _response.exec == 'SUCCESS'){
            console.info("[" + this._connection.contype + "] stop request to target: '" + this._target + "'");
        }
        else {
            console.error("[" + this._connection.contype + "] stop request to target '" + this._target + "' failed! (" + (_response ? _response.data : 'no response') + ")")
        }
    }
}

class InputScale extends WSInput {
    constructor(connection: WSConnection, private device: ScaleDevice){
        super(connection, 'SCALE');
    }

    async doIniRead(){
        if (!this.device){
            return null;    // no device provided 
        }

        return this._doIniRead({
            command: 'START',
            device: this.device.model,
            port: this.device.port || 'none'
        });
    }

    async doEndRead(){
        if (!this.device){
            return null;    // no device provided 
        }

        return this._doEndRead({
            command: 'STOP',
            device: this.device.model,
            port: this.device.port || 'none'
        });
    }
}


/*****************************/
/* SERVICE ABSTRACT CLASS    */
/*****************************/

abstract class _Service extends CustomLogger {
    protected host = null;
    protected port = null;
    
    abstract Connect(host, port, name) : Promise <WSConnection>;
} 

// WS Send timeouts: 10s print/CHECK; paytef 3 min; cashguard 5 min.
const WS_SEND_TIMEOUT_MS = {
    default: 10000,
    paytef: 180000,       // 3 min — mismo techo que paytef-direct MAX_POLLS
    cashguard: 300000,    // 5 min — introduce billetes con calma, sin 15 min
    cashlogy: 300000,     // 5 min — deposito/dispensa del ConnectorPlus, igual que cashguard
    prosegur: 300000,     // 5 min — deposito/dispensa MDW; el CHANGE bloquea mientras metes billetes
    glory: 300000,        // 5 min — Cashin+Cashout (CHANGE_BILL): el cajon espera a que metas el billete
    cashdro: 300000,      // 5 min — recicladora Azkoyen (Web Service): cobro/cambio esperan a que metas el efectivo
    backoffice: 1800000,  // 30 min — el Send del BACKOFFICE bloquea hasta que el
                          // usuario cierra la ventana en la maquina del cajon
};

function wsSendTimeoutMs(module: string, message?: any): number {
    const _m = (module || '').toLowerCase();
    // BACKOFFICE (ConnectorPlus) bloquea hasta que el usuario cierra la ventana:
    // techo mucho mas alto para no dar un falso fallo mientras sigue abierta.
    if (message && message.command === 'BACKOFFICE'){
        return WS_SEND_TIMEOUT_MS.backoffice;
    }
    if (_m === 'paytef'){
        return WS_SEND_TIMEOUT_MS.paytef;
    }
    // Cajones de dinero: operaciones largas (introducir billetes / dispensar).
    // El target del WS es el modelo del cajon, asi que enrutamos por ambos.
    if (_m === 'cashguard'){
        return WS_SEND_TIMEOUT_MS.cashguard;
    }
    if (_m === 'cashlogy'){
        return WS_SEND_TIMEOUT_MS.cashlogy;
    }
    if (_m === 'prosegur'){
        return WS_SEND_TIMEOUT_MS.prosegur;
    }
    if (_m === 'glory'){
        return WS_SEND_TIMEOUT_MS.glory;
    }
    if (_m === 'cashdro'){
        return WS_SEND_TIMEOUT_MS.cashdro;
    }
    return WS_SEND_TIMEOUT_MS.default;
}

function isIntermediateWslSendPayload(module: string, payload: any): boolean {
    if (!payload || typeof payload !== 'object'){
        return false;
    }
    const _status = payload['status'];
    if (_status === 'progress' || _status === 'queue_cleared' || _status === 'clearing_queue'){
        return true;
    }
    // ACK de alcance del cajón ({status:'ack'}): el print_service lo manda nada
    // más recibir el comando (check de conexión del WSR) y en LOCAL llega con el
    // MISMO UUID que la request. NO es el resultado -> hay que seguir esperando el
    // terminal real; si no, un Send (CONTENTS/BACKOFFICE/FINISH_REPLENISH...)
    // resolvería con el ACK vacío (p.ej. registrar 0 cargado con el cajón aún
    // trabajando). El handler de streaming ya lo ignora; aquí igualamos.
    if (_status === 'ack'){
        return true;
    }
    // paytef SALE/REFUND: 'started' antes del terminal approved/denied
    if (_status === 'started' && (module || '').toLowerCase() === 'paytef'){
        return true;
    }
    return false;
}

/*****************************/
/* LOCAL WS CONNECTION       */
/*****************************/

/*
    The connection is performed from the browser to the application (websocket server) running in localhost
    The connection is capable od sending and receiving messages to and from this server
*/

class WSConnectionL extends WSConnection {
    private ws: WebSocket = null;

    constructor(name, _ws){
        super('WSL', name);    // this is temporal: will be overwritten by the ws server
        
        this.ws = _ws;
        _ws.onmessage = (event) => {
            try {
                let _message_json = JSON.parse(event.data);
                if (_message_json){
                    // Standard envelope: nos basta con SERVICE_NAME para
                    // tratarlo como respuesta normal. Pedir MESSAGE_TYPE
                    // ademas era demasiado estricto — el server.py (gateway
                    // WSR) reenvia algunas respuestas con SERVICE_NAME +
                    // MESSAGE_UUID + MESSAGE_EXEC + MESSAGE_DATA pero SIN
                    // MESSAGE_TYPE, y esas caian en "Unknown format" rompiendo
                    // el match por UUID del Send().
                    if (_message_json['SERVICE_NAME']){
                        this._onMessageRecv.next({
                            name: _message_json['SERVICE_NAME'],    // service identifier configured in the ws
                            vers: _message_json['SERVICE_VERS'],    // service version configured in the ws
                            uuid: _message_json['MESSAGE_UUID'],    // message identifier (in case of response)
                            exec: _message_json['MESSAGE_EXEC'],    // execution result
                            data: _message_json['MESSAGE_DATA']     // message content (result or error message)
                        });
                    }
                    // Check if it's a direct progress message (from CashGuard process_transaction)
                    else if (_message_json['status'] && (_message_json['status'] == 'progress' || _message_json['status'] == 'success' || _message_json['status'] == 'error' || _message_json['status'] == 'cancelled')){
                        // This is a direct progress message from CashGuard
                        this._onMessageRecv.next({
                            name: 'CASHGUARD',    // service identifier
                            vers: '1.0.0',        // service version
                            uuid: null,           // no UUID for progress messages
                            exec: 'SUCCESS',      // execution result
                            data: _message_json  // the progress message itself
                        });
                    }
                    else {
                        // Unknown format, treat as raw data
                        this._onMessageRecv.next({
                            exec: 'SUCCESS',
                            data: event.data
                        });
                    }
                }
                else {
                    this._onMessageRecv.next({
                        exec: 'SUCCESS',                        // execution result
                        data: event.data                        // response is not a JSON object
                    });
                }
            } catch(e){
                // Not valid JSON, treat as raw data
                this._onMessageRecv.next({
                    exec: 'SUCCESS',
                    data: event.data
                });
            }
        };

        _ws.onclose = () => {
            if (this.ws){   // null if we have closed the session
                console.warn("[" + this.contype + "] Websocket connection lost");
                
                this._stopKeepAlive();
                this._onDisconnected.next();    
            }
        };

        this._startKeepAlive();     // keep-alive ping every 20 seconds
    }

    Disconnect(){
        // Socket compartido por el pool: un Disconnect() directo del dueno NO
        // cierra el socket, baja el refcount (otros pueden seguir usandolo). El
        // pool limpia este callback antes del cierre real.
        if (this._managedRelease){
            let _fn = this._managedRelease;
            _fn(this);
            return;
        }

        if (this.ws){
            this.ws.close();
            this.ws = null;

            this.addinfo("[" + this.contype + "] Websocket connection closed");
        }

        this._stopKeepAlive();
        super.Disconnect();
    }

    IsOpen(): boolean {
        return !!this.ws && (this.ws.readyState === WebSocket.OPEN);
    }

    // Fallo de TRANSPORTE (keepalive): el socket esta muerto. Debe cerrarse de
    // VERDAD y salir del pool, SIN pasar por la liberacion gestionada (que solo
    // bajaria el refcount y dejaria un socket zombie en el pool admitiendo
    // peticiones). Limpiamos el callback y cerramos crudo -> OnDisconnected
    // expulsa la entrada del pool.
    private _transportFailClose(){
        this._managedRelease = null;
        this.Disconnect();
    }

    Send(module, message){
        return new Promise <any> ((resolve) => {
            if (this.ws){
                let _uuidv4 = GenericUtils.uuidv4();
                let _sendmsg = {
                    UUIDV4: _uuidv4,
                    TARGET: module,
                    ACTION: message
                };

                // Timeout por modulo: print/CHECK 10s; paytef/cashguard minutos.
                let _sendTimeoutMs = wsSendTimeoutMs(module, message);
                let _resolved = false;
                let _timeout = null;
                let _subscription: any = null;
                const _finish = (value) => {
                    if (_resolved) return;
                    _resolved = true;
                    if (_timeout){
                        clearTimeout(_timeout);
                        _timeout = null;
                    }
                    if (_subscription){
                        _subscription.unsubscribe();
                        _subscription = null;
                    }
                    resolve(value);
                };

                _subscription = this.OnMessageRecv.subscribe(
                _response => {
                    if (_response && (_response['uuid'] == _uuidv4)){
                        // El Rust ahora envuelve los mensajes streaming
                        // intermedios (progress/queue_cleared/clearing_queue)
                        // del CashGuard con el MISMO MESSAGE_UUID que la
                        // request — necesario para que el server.py los
                        // enrute al cliente WSR. Pero el Send del cliente
                        // debe resolver SOLO cuando llega el terminal, no
                        // en el primer intermedio: si no, el modal cierra
                        // antes de cobrar (el queue_cleared llega en ms).
                        let _payload = _response['data'];
                        if (typeof _payload === 'string'){
                            try { _payload = JSON.parse(_payload); } catch(_){ _payload = null; }
                        }
                        if (_payload && typeof _payload === 'object'){
                            if (isIntermediateWslSendPayload(module, _payload)){
                                return; // intermedio — seguir esperando
                            }
                        }

                        if (_response.name != this.svcname){
                            console.warn("[" + this.contype + "] Local server configured for '" + _response.name + "' ('" + this.svcname + "' is expected)")
                        }

                        this._version = _response['vers'];
                        this._svcname = _response['name'];

                        _finish({
                            exec: _response['exec'],
                            data: _response['data']
                        });

                        if (_response['exec'] != 'SUCCESS'){
                             this.addwarn("[" + this.contype + "] " + _response['exec'] + " (" + _response['data'] + ")");
                        }
                    }
                });

                if (this.ws.readyState === WebSocket.OPEN){
                    // ws.send puede tirar excepcion incluso con readyState=OPEN
                    // (p.ej. buffer lleno, conexion cerrando justo en este
                    // microtask). Sin try/catch la excepcion aborta antes
                    // del setTimeout(10s) y el caller queda colgado para
                    // siempre porque el OnMessageRecv tampoco va a llegar.
                    try {
                        this.ws.send(JSON.stringify(_sendmsg));
                        this._onMessageSent.next(_sendmsg);
                        _timeout = setTimeout(() => {
                            console.warn("[" + this.contype + "] Send timeout " + (_sendTimeoutMs / 1000) + "s — no response for " + _uuidv4 + " (target=" + module + ")");
                            _finish(null);
                        }, _sendTimeoutMs);
                    } catch(e){
                        console.warn("[" + this.contype + "] ws.send threw — finishing with null", e);
                        _finish(null);
                    }
                }
                else {
                    _finish(null);  // send error
                }
            }
            else {
                resolve(null);     // not connected
            }
        })
    }

    private _keepalive_interval = null;
    private _keepalive_paused = false;
    private _wakeHandler: () => void = null;
    // _hiddenSince: cuando la pagina paso a hidden (para el umbral 5s del
    // wake handler — evita PINGs innecesarios en cambios de pestana rapidos
    // de escritorio).
    private _hiddenSince: number = null;
    // _lastPingAt: timestamp del ultimo PING (interval o wake). Se usa para
    // deduplicar: si el interval acaba de ticker y el wake dispara en el
    // mismo turn del event loop (iOS acumula ticks al despertar), evitamos
    // dos PINGs concurrentes que crearian race con dos setTimeouts distintos
    // y podrian causar Disconnect spurioso.
    private _lastPingAt = 0;

    private _startKeepAlive() {
        this._keepalive_interval = setInterval(() => {
            // Don't send PING if keepalive is paused (e.g., during active transaction)
            if (this._keepalive_paused){
                return;
            }

            this._lastPingAt = Date.now();
            let _timeout = setTimeout(() => {
                console.warn("[" + this.contype + "] ping request not satisfied after 5 seconds");
                this._transportFailClose();
            }, 5000);

            this.Send("BASE", "PING").then(
            _response => {
                clearTimeout(_timeout);
            })
        }, 20000);

        // iOS suspende setInterval en background. Al despertar el WS puede
        // seguir readyState=OPEN pero es zombie porque el server ya lo cerro
        // por su timeout. Sin este check el proximo Send espera hasta el
        // proximo tick del interval (hasta 20s) para detectarlo — la UI
        // queda colgada esperando una respuesta que no llega.
        // Fix: al volver visible tras >=5s oculto, disparar PING manual
        // inmediato. El umbral 5s evita PINGs innecesarios en cambios
        // rapidos de pestana en escritorio (tab switch < 1s).
        this._wakeHandler = () => {
            if (document.hidden) {
                this._hiddenSince = Date.now();
                return;
            }
            const _hiddenMs = this._hiddenSince != null ? Date.now() - this._hiddenSince : 0;
            this._hiddenSince = null;
            if (_hiddenMs < 5000 || !this.ws || this.ws.readyState !== WebSocket.OPEN){
                return;
            }
            if (this._keepalive_paused){
                return; // transaccion activa — el propio flow decide que hacer
            }
            // Dedup: si el interval acaba de disparar un PING (menos de 5s),
            // no lanzar otro. iOS acumula ticks del setInterval y los ejecuta
            // en batch al despertar; el wake seguiria ese mismo turn y
            // generaria dos PINGs concurrentes.
            if (Date.now() - this._lastPingAt < 5000){
                console.info("[" + this.contype + "] wake PING skip — interval PING reciente");
                return;
            }
            this._lastPingAt = Date.now();
            // 5s (no 3s): la radio de iOS tarda 3-5s en reactivarse tras el
            // unlock — con 3s matabamos conexiones sanas en cada desbloqueo.
            // Mismo margen que el PING del interval.
            const _wakeTimeout = setTimeout(() => {
                console.warn("[" + this.contype + "] wake PING not answered in 5s — reconnecting");
                this._transportFailClose();
            }, 5000);
            this.Send("BASE", "PING").then(_response => {
                clearTimeout(_wakeTimeout);
                // Send resuelve null cuando ws.send lanzo (buffer/cierre en
                // ese microtask) — el socket esta roto AHORA. Sin este
                // Disconnect, limpiar el timeout dejaba vivo el zombie que
                // el wake handler debia matar.
                if (!_response){
                    console.warn("[" + this.contype + "] wake PING failed — reconnecting");
                    this._transportFailClose();
                }
            });
        };
        document.addEventListener('visibilitychange', this._wakeHandler);
    }

    private _stopKeepAlive() {
        if (this._keepalive_interval) {
            clearInterval(this._keepalive_interval);
            this._keepalive_interval = null;
        }
        if (this._wakeHandler){
            document.removeEventListener('visibilitychange', this._wakeHandler);
            this._wakeHandler = null;
        }
        this._keepalive_paused = false;
    }

    // Public methods to pause/resume keepalive during active transactions
    PauseKeepAlive() {
        this._keepalive_paused = true;
        console.log("[" + this.contype + "] Keepalive paused");
    }
    
    ResumeKeepAlive() {
        this._keepalive_paused = false;
        console.log("[" + this.contype + "] Keepalive resumed");
    }    
};

class LocalService extends _Service {
    Connect(host, port, name) {
        this.host = host;
        this.port = port;

        let _url = "ws://" + this.host + ":" + this.port;
        return new Promise <WSConnection> ((resolve) => {
            if (!this.host || !this.port){
                console.info("[WSL] Websocket connection error (not provided)");
                return resolve(null);
            }
    
            let _ws = null;
            try {
                _ws = new WebSocket(_url);
            }
            catch(error){
                return resolve(null);     // connection failed
            }

            _ws.onerror = () => {
                console.warn("[WSL] Websocket connection error");
                resolve(null);          // connection failed (could not open connection)
            };                   

            _ws.onopen = () => {
                let _connection = new WSConnectionL(name, _ws);
                if (_connection){
                    _connection.service = this;

                    let _subscription = _connection.OnDisconnected.subscribe(
                    data => {
                        _subscription.unsubscribe();
                        resolve(null);      // connection closed
                    });        

                    _connection.Send("BASE", "CHECK").then(
                    _response => {
                        if (_response && _response.exec == 'SUCCESS'){
                            console.info("[WSL] Websocket connection ready");
                            resolve(_connection);   // connected
                        }
                        else {
                            // CHECK fallido: cerramos el WSConnectionL para
                            // que no se quede el keep-alive ni el socket vivo
                            // en intentos repetidos. Mejora independiente del
                            // dropdown — evita fugas si la BASE check falla.
                            try { _connection.Disconnect(); } catch(e){}
                            resolve(null);      // connection failed (invalid check)
                        }
                    });
                }
            };
        });
    }
}

/*****************************/
/* REMOTE WS CONNECTION      */
/*****************************/

/*
    The connection is performed from the wsr REST server to the wss (websocket server) running hosted
    The local websocket server has registered to the remote wss server that will act as gateway
    Messages sent to the wsr REST server will be sent to the wss server and then back to the targeted web socket server
    Through this method, the connection is capable od sending single messages to the local running server 
*/

class WSConnectionR extends WSConnection {
    constructor(name, private http: httpService){
        super('WSR', name);
    }

    Disconnect(){
        super.Disconnect();
    }

    Send(module, message){
        return new Promise <any> (async (resolve) => {
            let _url = AppConstants.restURL + "send?service=" + this.svcname;

            if (!this.http.IsOnline){
                return resolve(null);
            }

            // AbortController + timeout 30s: sin este cinturon, un fetch
            // suspendido durante iOS sleep puede quedarse colgado eternamente
            // tras despertar (bug conocido de Safari con fetch de larga vida).
            // El await sin salida bloquea al caller de Send (impresion,
            // scale, RFID) esperando una response que nunca llega. 30s cubre
            // holgado la mayoria de operaciones locales; los timeouts largos
            // no deberian ir por WSConnectionR (fetch HTTP) sino por WS.
            //
            // Guard AbortController: existe desde Safari 12.1 (iOS 12.2),
            // Chrome 66, Firefox 57. Fetch existe desde antes (Safari 10.1)
            // — en ese hueco (Safari 10.1-12.0 / iOS 10-12.1) `new
            // AbortController()` lanza ReferenceError sincrono y rompe el
            // Send antes de iniciar el fetch. Fallback: sin abort, con
            // Promise.race para respetar el timeout (el fetch podria
            // seguir en el aire tras el timeout — imperfecto pero no rompe
            // el caller).
            const _hasAbortController = typeof AbortController !== 'undefined';
            const _abort = _hasAbortController ? new AbortController() : null;
            let _timedOut = false;
            // Timeout por modulo (mismo mapa que los WS): 30s por defecto —cubre
            // print/scale/RFID/etc.—, pero los cajones de dinero
            // (PROSEGUR/CASHGUARD/CASHLOGY) y paytef necesitan MUCHO mas: el
            // deposito de billetes A PLAZOS (5+5+5) supera 30s y el fetch abortaba
            // dejando el cobro DESALINEADO (el RS seguia depositando pero el front
            // perdia la respuesta). Por eso el comandero (WSR) solo cobraba si
            // metias el importe del tiron. Math.max mantiene el minimo de 30s.
            const _wsrTimeoutMs = Math.max(30000, wsSendTimeoutMs(module, message));
            const _abortTimeout = setTimeout(() => {
                _timedOut = true;
                console.warn("[WSR] fetch timeout " + (_wsrTimeoutMs / 1000) + "s — " + (_abort ? "aborting" : "timeout sin abort") + " (" + _url + ")");
                if (_abort){
                    _abort.abort();
                }
            }, _wsrTimeoutMs);

            const _fetchOptions: any = {
                method: 'POST',
                headers: {
                    'Content-Type': 'text/plain',
                    'Accept': 'application/json'
                },
                body: JSON.stringify({ TARGET: module, ACTION: message })
            };
            if (_abort){
                _fetchOptions.signal = _abort.signal;
            }

            // Interval del race manual (solo navegadores sin AbortController).
            // Declarado fuera del try para poder limpiarlo tanto en exito
            // como en error — sin esto, un fetch que termina antes del
            // timeout dejaba el setInterval vivo cada 500ms para siempre.
            let _raceInterval: ReturnType<typeof setInterval> = null;
            const _clearRaceInterval = () => {
                if (_raceInterval != null){
                    clearInterval(_raceInterval);
                    _raceInterval = null;
                }
            };

            try {
                let _fetchPromise: Promise<Response> = fetch(_url, _fetchOptions);
                // En navegadores sin AbortController, hacemos race manual:
                // el timeout resuelve la Promise externa con null aunque el
                // fetch siga vivo. En los con AbortController, el signal ya
                // se encarga y este race es redundante pero inofensivo.
                let response = _abort
                    ? await _fetchPromise
                    : await Promise.race([
                        _fetchPromise,
                        new Promise<Response>((_, reject) => {
                            _raceInterval = setInterval(() => {
                                if (_timedOut){
                                    _clearRaceInterval();
                                    reject(new Error("timeout"));
                                }
                            }, 500);
                        })
                    ]);

                if (response.ok) {
                    // El cinturon de 30s sigue ARMADO durante response.json():
                    // un body que se atasca tras recibir headers (iOS duerme
                    // justo despues del fetch resolve) colgaria el await sin
                    // limite; el abort() del timeout tambien rechaza el read
                    // del body con AbortError → cae al catch.
                    let _response = await response.json();

                    _clearRaceInterval();
                    clearTimeout(_abortTimeout);

                    this._version = _response['vers'];
                    this._svcname = _response['name'];

                    resolve({
                        exec: _response['exec'],
                        data: _response['data']
                    });

                    if (_response['exec'] != 'SUCCESS'){
                        this.addwarn("[" + this.contype + "] " + _response['exec'] + " (" + _response['data'] + ")");
                    }
                }
                else {
                    _clearRaceInterval();
                    clearTimeout(_abortTimeout);
                    resolve(null);      // error on execution
                }
            }
            catch (e) {
                _clearRaceInterval();
                clearTimeout(_abortTimeout);
                if ((e && e.name === 'AbortError') || (e && e.message === 'timeout')){
                    // TIMEOUT ≠ fallo de conexion. El request PUDO haber
                    // llegado al print service (response lenta, no perdida).
                    // Resolver con marker truthy exec:'TIMEOUT' en vez de null:
                    // _wsRemoteSend (print.ts:556-561) solo cae al fallback de
                    // impresion via server cuando el valor es falsy — con el
                    // marker evitamos re-imprimir un ticket que probablemente
                    // ya salio (doble ticket fisico / doble apertura de cajon).
                    // Coherente con el comportamiento existente: un response
                    // exec:'ERROR' del servicio (alcanzable pero fallido)
                    // tampoco dispara el fallback.
                    console.warn("[WSR] fetch timeout (" + _url + ") — entrega incierta, sin fallback");
                    resolve({ exec: 'TIMEOUT', data: null });
                }
                else {
                    // Fallo de conexion real (refused, DNS, offline): el
                    // request NO llego. resolve(null) → los callers caen al
                    // fallback (imprimir via server) con seguridad.
                    console.error("[WSR] Failed to fetch (" + _url + ")");
                    resolve(null);
                }
            }
        });
    }
};

/**
 * WSR via WebSocket — opt-in, dedicado a streaming.
 *
 * Solo se usa cuando un caller especifico (el modal de CashGuard) pide
 * upgrade a stream con `RemoteService.ConnectStreaming(name)`. El resto
 * del flujo WSR (prints, scale, RFID, etc.) sigue por HTTP via la
 * WSConnectionR de toda la vida — cero cambio de comportamiento.
 *
 * Apache necesita mod_proxy_wstunnel para /ws. Si el upgrade falla, el
 * caller hace fallback a HTTP. La WSR HTTP sigue funcionando con su
 * parche actual para procesar el terminal del CashGuard.
 */
class WSConnectionRStream extends WSConnection {
    private ws: WebSocket = null;

    constructor(name, _ws: WebSocket){
        super('WSR', name);

        this.ws = _ws;
        _ws.onmessage = (event) => {
            try {
                let _message_json = JSON.parse(event.data);
                if (_message_json){
                    // Mismo razonamiento que en WSConnectionL — solo
                    // SERVICE_NAME basta. access.py /ws si incluye siempre
                    // MESSAGE_TYPE, pero ser defensivo no cuesta nada y
                    // evita un branch "Unknown format" si en algun futuro
                    // mensaje llega sin el campo.
                    if (_message_json['SERVICE_NAME']){
                        this._onMessageRecv.next({
                            name: _message_json['SERVICE_NAME'],
                            vers: _message_json['SERVICE_VERS'],
                            uuid: _message_json['MESSAGE_UUID'],
                            exec: _message_json['MESSAGE_EXEC'],
                            data: _message_json['MESSAGE_DATA']
                        });
                    }
                    else if (_message_json['status'] && (_message_json['status'] == 'progress' || _message_json['status'] == 'success' || _message_json['status'] == 'error' || _message_json['status'] == 'cancelled')){
                        this._onMessageRecv.next({
                            name: 'CASHGUARD',
                            vers: '1.0.0',
                            uuid: null,
                            exec: 'SUCCESS',
                            data: _message_json
                        });
                    }
                    else {
                        this._onMessageRecv.next({
                            exec: 'SUCCESS',
                            data: event.data
                        });
                    }
                }
                else {
                    this._onMessageRecv.next({
                        exec: 'SUCCESS',
                        data: event.data
                    });
                }
            } catch(e){
                this._onMessageRecv.next({
                    exec: 'SUCCESS',
                    data: event.data
                });
            }
        };

        _ws.onclose = () => {
            if (this.ws){
                console.warn("[WSR] streaming websocket connection lost");
                this._stopKeepAlive();
                this._onDisconnected.next();
            }
        };

        this._startKeepAlive();
    }

    Disconnect(){
        if (this.ws){
            this.ws.close();
            this.ws = null;
            this.addinfo("[WSR] streaming websocket closed");
        }
        this._stopKeepAlive();
        super.Disconnect();
    }

    Send(module, message){
        return new Promise <any> ((resolve) => {
            if (!this.ws){
                return resolve(null);
            }
            let _uuidv4 = GenericUtils.uuidv4();
            let _sendmsg = {
                UUIDV4: _uuidv4,
                TARGET: module,
                ACTION: message
            };

            let _resolved = false;
            let _timeout = null;
            let _subscription: any = null;
            let _wakeSub: () => void = null;
            // Wall-clock deadline para el timeout: setTimeout(900000) pausa
            // en iOS background, asi que al despertar podemos llevar horas
            // de wall clock pero solo minutos de "perceived time". Guardamos
            // la deadline absoluta para poder cerrar el Send inmediatamente
            // al volver visible si ya paso el limite real.
            let _deadline: number = null;
            const _finish = (value) => {
                if (_resolved) return;
                _resolved = true;
                if (_timeout){ clearTimeout(_timeout); _timeout = null; }
                if (_subscription){ _subscription.unsubscribe(); _subscription = null; }
                if (_wakeSub){
                    document.removeEventListener('visibilitychange', _wakeSub);
                    window.removeEventListener('pageshow', _wakeSub);
                    _wakeSub = null;
                }
                resolve(value);
            };

            _subscription = this.OnMessageRecv.subscribe(_response => {
                if (_response && (_response['uuid'] == _uuidv4)){
                    // Filtrar intermedios — ver comentario equivalente en
                    // WSConnectionL.Send. Sin esto el Send resuelve en el
                    // primer progress/queue_cleared y el modal del cobro
                    // cierra antes de tiempo.
                    let _payload = _response['data'];
                    if (typeof _payload === 'string'){
                        try { _payload = JSON.parse(_payload); } catch(_){ _payload = null; }
                    }
                    if (_payload && typeof _payload === 'object'){
                        let _status = _payload['status'];
                        if (_status === 'progress' || _status === 'queue_cleared' || _status === 'clearing_queue'){
                            return; // intermedio — seguir esperando terminal
                        }
                        // ACK de alcance ({status:'ack'}): no es el resultado (el
                        // server.py ya lo filtra en remoto, pero por si llegara).
                        if (_status === 'ack'){
                            return;
                        }
                    }

                    this._version = _response['vers'];
                    this._svcname = _response['name'] || this._svcname;
                    _finish({
                        exec: _response['exec'],
                        data: _response['data']
                    });
                    if (_response['exec'] != 'SUCCESS'){
                        this.addwarn("[WSR] " + _response['exec'] + " (" + _response['data'] + ")");
                    }
                }
            });

            if (this.ws.readyState === WebSocket.OPEN){
                try {
                    this.ws.send(JSON.stringify(_sendmsg));
                    this._onMessageSent.next(_sendmsg);
                    let _sendTimeoutMs = wsSendTimeoutMs(module, message);
                    _deadline = Date.now() + _sendTimeoutMs;
                    _timeout = setTimeout(() => {
                        console.warn("[WSR] Send timeout " + (_sendTimeoutMs / 1000) + "s — no response for " + _uuidv4 + " (target=" + module + ")");
                        _finish(null);
                    }, _sendTimeoutMs);
                    // iOS wake: al volver visible, si el wall clock supero
                    // la deadline mientras estabamos en background (timer
                    // suspendido), cerrar el Send inmediato en vez de esperar
                    // a que el setTimeout "recupere" el tiempo perdido.
                    //
                    // Escuchar visibilitychange (unlock pantalla) Y pageshow
                    // (bfcache Safari back/forward — NO dispara visibilitychange
                    // porque la pagina "ya estaba visible" al congelarse):
                    // cajero navega back durante PROCESS_TRANSACTION, vuelve
                    // 30min despues → pageshow persisted=true → chequeamos
                    // deadline igual que en visibilitychange.
                    // Grace period de 5s (no _finish(null) inmediato): si la
                    // transaccion COMPLETO en la maquina mientras dormiamos,
                    // el frame SUCCESS quedo bufferizado en el WS y el
                    // visibilitychange puede procesarse ANTES que el onmessage
                    // encolado. Matar el Send sincronamente descartaria un
                    // cobro real ya aceptado (descuadre de caja). Con la
                    // gracia, el onmessage bufferizado gana la carrera via
                    // _finish(response) y el timer de gracia no-opea gracias
                    // al guard _resolved.
                    let _graceTimer: ReturnType<typeof setTimeout> = null;
                    _wakeSub = () => {
                        if (document.hidden) return;
                        if (_deadline != null && Date.now() > _deadline && _graceTimer == null){
                            console.warn("[WSR] Wall-clock deadline pasado durante sleep — gracia 5s para response bufferizada de " + _uuidv4);
                            _graceTimer = setTimeout(() => {
                                _finish(null);
                            }, 5000);
                        }
                    };
                    document.addEventListener('visibilitychange', _wakeSub);
                    window.addEventListener('pageshow', _wakeSub);
                } catch(e){
                    console.warn("[WSR] ws.send threw — finishing with null", e);
                    _finish(null);
                }
            }
            else {
                _finish(null);
            }
        });
    }

    private _keepalive_interval = null;
    private _keepalive_paused = false;
    private _wakeHandler: () => void = null;
    private _hiddenSince: number = null;
    private _lastPingAt = 0;

    private _startKeepAlive(){
        this._keepalive_interval = setInterval(() => {
            if (this._keepalive_paused) return;
            this._lastPingAt = Date.now();
            let _timeout = setTimeout(() => {
                console.warn("[WSR] streaming ping not satisfied after 5 seconds");
                this.Disconnect();
            }, 5000);
            this.Send("BASE", "PING").then(_response => {
                clearTimeout(_timeout);
            });
        }, 20000);

        // iOS wake: ver comentario extendido en WSConnectionL._startKeepAlive.
        // Umbral 5s: evita PINGs innecesarios en cambios de pestana en escritorio.
        this._wakeHandler = () => {
            if (document.hidden) {
                this._hiddenSince = Date.now();
                return;
            }
            const _hiddenMs = this._hiddenSince != null ? Date.now() - this._hiddenSince : 0;
            this._hiddenSince = null;
            if (_hiddenMs < 5000 || !this.ws || this.ws.readyState !== WebSocket.OPEN){
                return;
            }
            if (this._keepalive_paused){
                return;
            }
            // Dedup vs interval catchup — ver WSConnectionL._startKeepAlive.
            if (Date.now() - this._lastPingAt < 5000){
                console.info("[WSR] wake PING skip — interval PING reciente");
                return;
            }
            this._lastPingAt = Date.now();
            // 5s + Disconnect-on-null: ver comentario en el wake handler de
            // WSConnectionL._startKeepAlive.
            const _wakeTimeout = setTimeout(() => {
                console.warn("[WSR] wake PING not answered in 5s — reconnecting");
                this.Disconnect();
            }, 5000);
            this.Send("BASE", "PING").then(_response => {
                clearTimeout(_wakeTimeout);
                if (!_response){
                    console.warn("[WSR] wake PING failed — reconnecting");
                    this.Disconnect();
                }
            });
        };
        document.addEventListener('visibilitychange', this._wakeHandler);
    }

    private _stopKeepAlive(){
        if (this._keepalive_interval){
            clearInterval(this._keepalive_interval);
            this._keepalive_interval = null;
        }
        if (this._wakeHandler){
            document.removeEventListener('visibilitychange', this._wakeHandler);
            this._wakeHandler = null;
        }
        this._keepalive_paused = false;
    }

    PauseKeepAlive(){
        this._keepalive_paused = true;
    }

    ResumeKeepAlive(){
        this._keepalive_paused = false;
    }
};

class RemoteService extends _Service {

    constructor(private http: httpService){
        super();
    }

    Connect(host, port, name){
        this.host = host;
        this.port = port;

        return new Promise <WSConnection> ((resolve) => {
            let _connection = new WSConnectionR(name, this.http);
            if (_connection){
                _connection.service = this;

                _connection.Send("BASE", "CHECK").then(
                _response => {
                    if (_response && _response.exec == 'SUCCESS'){
                        this.addinfo("[WSR] Websocket connection ready");
                        resolve(_connection);   // connected
                    }
                    else {
                        resolve(null);          // connection failed (invalid check)
                    }
                });
            }
        });
    }

    /**
     * Opt-in: abre un WS dedicado al endpoint /ws de access.py para
     * recibir streaming (progress + terminal en tiempo real). Solo lo
     * usa el modal de CashGuard cuando el cliente esta en WSR — el resto
     * de servicios (prints, scale, RFID, ...) sigue por HTTP.
     *
     * Resuelve a null si:
     *  - Apache no proxea WebSocket (mod_proxy_wstunnel ausente)
     *  - access.py no expone /ws (version antigua del server)
     *  - timeout 4s (red lenta o caida)
     *
     * En cualquier caso de null, el caller debe seguir con su WSConnectionR
     * HTTP (no abrir una segunda HTTP — usar la existente).
     */
    ConnectStreaming(name: string): Promise<WSConnection> {
        return new Promise<WSConnection> ((resolve) => {
            let _url = AppConstants.wssURL + "?service=" + encodeURIComponent(name);
            let _ws: WebSocket;
            try {
                _ws = new WebSocket(_url);
            } catch(e){
                console.warn("[WSR] streaming WebSocket ctor threw:", e);
                return resolve(null);
            }

            let _settled = false;
            const _settle = (value: WSConnection) => {
                if (_settled) return;
                _settled = true;
                resolve(value);
            };

            let _timeout = setTimeout(() => {
                console.warn("[WSR] /ws upgrade timeout 4s");
                try { _ws.close(); } catch(_){ /* noop */ }
                _settle(null);
            }, 4000);

            _ws.onerror = () => {
                clearTimeout(_timeout);
                console.warn("[WSR] /ws upgrade error");
                _settle(null);
            };

            _ws.onopen = () => {
                clearTimeout(_timeout);
                let _connection = new WSConnectionRStream(name, _ws);
                _connection.service = this;
                // BASE/CHECK lo responde access.py en local sin viajar al
                // rasppi — valida que envelope cliente/servidor funciona.
                _connection.Send("BASE", "CHECK").then(_response => {
                    if (_response && _response.exec == 'SUCCESS'){
                        this.addinfo("[WSR] streaming websocket ready");
                        _settle(_connection);
                    }
                    else {
                        try { _ws.close(); } catch(_){ /* noop */ }
                        _settle(null);
                    }
                });
            };
        });
    }
}

/*****************************/
/* DEVICES SERVICE           */
/*****************************/

@Injectable()
export class deviceService {

    private _localservice = null;
    get localService(){
        if (!this._localservice){
            this._localservice = new LocalService()
        }
        return this._localservice;
    }

    private _rmoteservice = null;
    get rmoteService(){
        if (!this._rmoteservice){
            this._rmoteservice = new RemoteService(this.http)
        }
        return this._rmoteservice;
    }

    /****************************/
    /* STARTS HERE              */
    /****************************/

    constructor(private http: httpService){
        // nothing to do
    }    

    /****************************/
    /* NAME AVAILABILITY        */
    /****************************/

    private _freeServiceUrl = 'sockets/available.php';

    Available(sync: syncService,  placeid, service){
        return new Promise((resolve) => {
            sync.DoRequest(this._freeServiceUrl, { place: placeid, service: service })
            .then(data => {
                resolve(data);
            }, error => {
                if (error.status != 0){
                    console.error("[REQUEST] Error [" + error.status + "] in http request '" + this._freeServiceUrl + "'");
                }

                resolve(false);
            }); 
        });
    }

    /****************************/
    /* CONNECT CONTROL          */
    /****************************/

    // Tracking de conexiones locales activas — el dropdown de impresoras
    // (FetchAvailablePrinters) lo usa para reutilizar cualquier socket
    // local ya abierto en la app, evitando montar/cerrar conexion por cada
    // apertura del select.
    private _activeLocalConnections = new Set<WSConnection>();

    // POOL OPT-IN de conexiones LOCALES persistentes request/response (CACHE,
    // PRINT, STATUS del cajon) al MISMO till: en vez de abrir un socket por cada
    // una, comparten UNO. Solo entran las que pasan `pooled=true`; TODO lo demas
    // (cobro, paytef, bascula, one-off...) mantiene su socket DEDICADO como
    // siempre -> imposible cruzar mensajes (esos consumidores leen OnMessageRecv
    // sin filtrar por UUID). El WSR (tolocal=false) NUNCA entra al pool.
    //   - Connect(pooled=true): reaprovecha el socket abierto por host|port
    //     (refcount++); si hay uno EN VUELO espera a ese (sin duplicar por
    //     carrera); si no, crea uno nuevo.
    //   - Disconnect(): baja el refcount y, al llegar a 0, cierra DIFERIDO
    //     (ventana de gracia) para reaprovechar; si se reutiliza antes, se
    //     cancela. Los duenos DEBEN liberar por deviceService.Disconnect().
    //   - Si el socket muere (OnDisconnected), se expulsa del pool.
    // Compartir es seguro aqui: son request/response correladas por UUID.
    private _localPool = new Map<string, { key: string, connection: WSConnection, pending: Promise<WSConnection>, refcount: number, idle: any }>();
    private _poolByConn = new Map<WSConnection, { key: string, connection: WSConnection, pending: Promise<WSConnection>, refcount: number, idle: any }>();
    private static readonly POOL_IDLE_MS = 30000;   // gracia antes de cerrar un socket ocioso

    // Clave SOLO por host+puerto (no por name): el socket va a `ws://host:port` y
    // el name es una etiqueta cliente; el enrutado real es por TARGET+UUID.
    private _poolKey(host, port): string {
        return String(host) + '|' + String(port);
    }

    private _poolEvict(entry){
        if (!entry){ return; }
        if (entry.idle){ clearTimeout(entry.idle); entry.idle = null; }
        if (this._localPool.get(entry.key) === entry){ this._localPool.delete(entry.key); }
        if (entry.connection){
            this._poolByConn.delete(entry.connection);
            this._activeLocalConnections.delete(entry.connection);
            // Quitar la liberacion gestionada: ya no esta en el pool, asi que un
            // Disconnect() posterior del dueno debe cerrar crudo, no reentrar al
            // callback (que causaria recursion infinita -> stack overflow).
            entry.connection.SetManagedRelease(null);
        }
    }

    async Connect(host, port, service, tolocal, pooled = false): Promise <WSConnection> {
        if (!tolocal){
            return await this.rmoteService.Connect(host, port, service);
        }

        if (!pooled){
            // Comportamiento de SIEMPRE: socket dedicado por operacion. No se
            // agrupa, asi que es imposible cruzar mensajes con otra operacion.
            let _connection = await this.localService.Connect(host, port, service);
            if (_connection){
                this._activeLocalConnections.add(_connection);
                let _sub = _connection.OnDisconnected.subscribe(() => {
                    this._activeLocalConnections.delete(_connection);
                    _sub.unsubscribe();
                });
            }
            return _connection;
        }

        // pooled=true -> compartir socket por host|port (solo request/response).
        let _key = this._poolKey(host, port);
        let _entry = this._localPool.get(_key);

        if (_entry){
            if (_entry.connection && _entry.connection.IsOpen()){
                _entry.refcount++;
                if (_entry.idle){ clearTimeout(_entry.idle); _entry.idle = null; }
                return _entry.connection;
            }
            if (_entry.pending){
                // Conexion EN VUELO: esperarla en vez de abrir otra (evita el
                // duplicado por carrera de dos Connect concurrentes).
                _entry.refcount++;
                let _c = await _entry.pending;
                if (_c && _c.IsOpen()){
                    return _c;   // OK: nos quedamos el refcount que subimos.
                }
                // La conexion en vuelo FALLO. Soltamos el refcount y devolvemos
                // null en vez de caer al bloque de creacion: si varios waiters
                // cayeran a crear a la vez, cada uno abriria su socket
                // (duplicados). El proximo Connect NO concurrente creara una
                // nueva; los callers (CACHE/PRINT) ya toleran null (reintentan).
                _entry.refcount--;
                return null;
            }
        }

        // Crear una nueva; guardamos su promesa en vuelo para las concurrentes
        // que lleguen mientras conecta.
        let _pending = this.localService.Connect(host, port, service);
        _entry = { key: _key, connection: null, pending: _pending, refcount: 1, idle: null };
        this._localPool.set(_key, _entry);

        let _connection = await _pending;
        _entry.pending = null;
        if (!_connection){
            this._poolEvict(_entry);
            return null;
        }
        _entry.connection = _connection;
        this._poolByConn.set(_connection, _entry);
        this._activeLocalConnections.add(_connection);
        // Un Disconnect() directo del dueno se redirige a la liberacion por
        // refcount (no tumba el socket que otros aun usan).
        _connection.SetManagedRelease((c) => this.Disconnect(c));

        let _sub = _connection.OnDisconnected.subscribe(() => {
            this._poolEvict(_entry);
            _sub.unsubscribe();
        });

        return _connection;
    }

    GetAnyLocalConnection() : WSConnection {
        for (let _conn of this._activeLocalConnections){
            if (_conn.IsOpen()){ return _conn; }
        }
        return null;
    }

    /**
     * Opt-in: para flujos que necesitan streaming en WSR (progress + terminal).
     * Lo usa SOLO el modal de CashGuard. Resuelve null si Apache no proxea
     * WebSocket o access.py no expone /ws — el caller debe seguir con su
     * conexion WSConnectionR HTTP de toda la vida.
     */
    ConnectRemoteStreaming(service: string): Promise <WSConnection> {
        return this.rmoteService.ConnectStreaming(service);
    }

    Disconnect(_connection){
        if (!_connection){ return; }

        let _entry = this._poolByConn.get(_connection);
        if (!_entry){
            // No es del pool (remoto, o ya expulsado): cerrar como siempre.
            this._activeLocalConnections.delete(_connection);
            _connection.Disconnect();
            return;
        }

        if (_entry.refcount > 0){ _entry.refcount--; }
        if (_entry.refcount > 0){
            return;   // otra operacion sigue usando el socket: no cerrar.
        }

        // Refcount 0: cierre DIFERIDO. Mantener el socket una ventana de gracia
        // para que el siguiente cobro lo reaproveche; reactivamos su keepalive
        // para que no muera ocioso. Si nadie lo usa antes del timeout, se cierra.
        _entry.connection.ResumeKeepAlive();
        if (_entry.idle){ clearTimeout(_entry.idle); }
        _entry.idle = setTimeout(() => {
            _entry.idle = null;
            if (_entry.refcount <= 0){
                let _c = _entry.connection;
                this._poolEvict(_entry);
                // Limpiar la liberacion gestionada ANTES del cierre real para que
                // este Disconnect() SI cierre el socket (no vuelva al refcount).
                if (_c){ _c.SetManagedRelease(null); _c.Disconnect(); }
            }
        }, deviceService.POOL_IDLE_MS);
    }

    /****************************/
    /* CONNECTION CHECK         */
    /****************************/

    async Test(host, port, service, tolocal) : Promise <WSConnection> {
        let _success = null;

        if (tolocal){
            let _connection = await this.localService.Connect(host, port, service);
            if (_connection){
                this.Disconnect(_connection);
                _success = _connection;
            }
        }
        else {
            let _connection = await this.rmoteService.Connect(host, port, service);
            if (_connection){
                this.Disconnect(_connection);
                _success = _connection;
            }
        }

        return _success;
    }

    /****************************/
    /* PRINTER ACTIONS          */
    /****************************/

    async doOpen(connection: WSConnection, device){
        if (!connection || !connection.service){
            return;
        }

        let _message = {};
        
        _message[device] = {
            print: null,
            copies: 0,
            open: true
        }
        
        let _response = await connection.Send("PRINT", JSON.stringify(_message));
        if (_response && _response.exec == 'SUCCESS'){
            console.info("[" + connection.contype + "] open request sent to device '" + device + "'")
        }
        else {
            console.error("[" + connection.contype + "] open request to device '" + device + "' failed! (" + (_response ? _response.data : 'no response') + ")")
        }

        return _response;
    }

    async doPrint(connection, device, html, copies = 1){
        if (!connection || !connection.service){
            return;
        }

        let _message = {};

        _message[device] = {
            print: GenericUtils.stringToBase64(html),
            copies: copies,
            open: false
        }

        let _response = await connection.Send("PRINT", JSON.stringify(_message));
        if (_response && _response.exec == 'SUCCESS'){
            console.info("[" + connection.contype + "] print request sent to device '" + device + "'")
        }
        else {
            console.error("[" + connection.contype + "] print request to device '" + device + "' failed! (" + (_response ? _response.data : 'no response') + ")")
        }

        return _response;
    }

    /**
     * Pide al CommandViewer del rasppi la lista de impresoras instaladas.
     * El servicio responde con un array de strings (nombres de dispositivos)
     * que el dropdown del componente ui-printer rellena.
     */
    async doGetPrinters(connection: WSConnection) : Promise <Array<string>> {
        if (!connection){
            return [];
        }

        let _response = await connection.Send("PRINT", "getPrinters");
        if (_response && _response.exec == 'SUCCESS' && Array.isArray(_response.data)){
            return _response.data;
        }

        return [];
    }

    /****************************/
    /* CACHE BACKUP             */
    /****************************/

    async WSStorage(sync: syncService){
        let host = null;
        let port = null;
        let name = null;

        let _place = sync.Session.place;
        if (_place && _place.till){
            host = _place.till.wshost;
            port = _place.till.wsport;
            name = _place.till.name;
        }

        if (host && port && name){
            // CACHE: conexion persistente request/response -> POOLED (comparte el
            // socket del till con PRINT). El STATUS del cajon NO se poolea
            // (acepta broadcasts sin uuid) -> va por su socket dedicado aparte.
            let _connection = await this.Connect(host, port, name, true, true);
            if (_connection){
                return new WSCacheStorage(_connection);
            }
        }

        return null;    // not connected
    }

    /****************************/
    /* INPUT DEVICES            */
    /****************************/

    async WSInputScale(host, port, name, device: ScaleDevice){
        let _connection = await this.Connect(host, port, name, true);
        if (_connection){
            return new InputScale(_connection, device);
        }

        return null;    // not connected
    }

}

